import { useTranslate } from '@repo/shared/hooks'
import { z } from 'zod'

interface OrderBoxSchemaProps {
  tab: 'buy' | 'sell'
  priceType: 'market' | 'limit'
  availableBalance: number
  instantPrice: number
  inputCurrency: 'IRR' | 'TMN'
  outputCurrency?: 'IRR' | 'TMN'
  maxTokenAmount: number
  minOrderSize?: number
  targetScale: number
}

export const useOrderBoxSchema = (props: OrderBoxSchemaProps) => {
  const { t } = useTranslate()

  const schema = z
    .object({
      amount: z
        .string({
          required_error: t('order_amount_required'),
        })
        .min(1, { message: t('order_amount_required') })
        .refine(
          (val) => {
            if (props?.minOrderSize && props?.minOrderSize > 0) {
              return Number(val) >= props?.minOrderSize / props.targetScale
            }
            return true
          },
          {
            message: t('order_amount_min_order_size', {
              min: props?.minOrderSize
                ? props?.minOrderSize / props.targetScale
                : 0,
            }),
          },
        ),
      price: z.number().optional(),
      activationPrice: z.number().optional(),
      total: z.number().optional(),
      range: z.number().optional(),
      rangeIRRAmount: z.number().optional(),
    })
    .superRefine((data, ctx) => {
      if (!props || props?.tab !== 'buy') return

      // Calculate total including fee
      const price =
        props?.priceType === 'market' ? props?.instantPrice : data.price || 0
      const total = Number(data.amount) * price
      const fee = Math.floor(total * 0.01)
      let totalWithFee = total + fee

      // Apply currency conversion if needed
      if (props?.inputCurrency === 'IRR' && props?.outputCurrency === 'TMN') {
        totalWithFee = totalWithFee / 10
      }

      if (totalWithFee > props?.availableBalance) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('order_insufficient_balance'),
          path: ['amount'],
        })
      }
    })

  return { schema }
}
